/**
 * MCPClientDrawer — right-side sheet showing the full profile of an
 * MCP OAuth client (scopes, redirect URIs, recent activity, identity).
 */
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ScopeChip } from "./ScopeChip";
import { formatDistanceToNow } from "date-fns";
import type { McpClientDTO } from "@/types/edrApi";

export interface McpClientDrawerProps {
  client: McpClientDTO | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function McpClientDrawer({ client, open, onOpenChange }: McpClientDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{client?.displayName ?? "MCP client"}</SheetTitle>
          <SheetDescription>
            {client?.clientType ? `Registered as ${client.clientType}` : "Client profile"}
            {client?.id ? ` · ${client.id}` : ""}
          </SheetDescription>
        </SheetHeader>

        {client && (
          <div className="mt-6 space-y-6 text-sm">
            <Section title="Identity">
              <Row label="Display name" value={client.displayName ?? "—"} />
              <Row label="Client ID" mono value={client.id ?? "—"} />
              <Row label="Type" value={client.clientType ?? "—"} />
              <Row label="Issuer" mono value={client.issuer ?? "—"} />
              <Row label="Status" value={client.status ?? "active"} />
            </Section>

            <Section title="Registration">
              <Row
                label="Registered"
                value={
                  client.registeredAt
                    ? `${formatDistanceToNow(new Date(client.registeredAt), { addSuffix: true })}`
                    : "—"
                }
              />
              <Row label="Via" value={client.registeredVia ?? "—"} />
              <Row label="By" value={client.registeredBy ?? "—"} />
            </Section>

            <Section title="Activity">
              <Row
                label="Last used"
                value={
                  client.lastUsedAt
                    ? formatDistanceToNow(new Date(client.lastUsedAt), { addSuffix: true })
                    : "Never"
                }
              />
              <Row label="Last tool" mono value={client.lastUsedTool ?? "—"} />
              <Row label="Last IP" mono value={client.lastUsedIP ?? "—"} />
              <Row label="Calls (24h)" value={(client.callsLast24h ?? 0).toLocaleString()} />
              <Row label="Calls (7d)" value={(client.callsLast7d ?? 0).toLocaleString()} />
            </Section>

            <Section title="Scopes">
              {client.scopes && client.scopes.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {client.scopes.map((s) => (
                    <ScopeChip key={s} scope={s} />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No scopes granted.</p>
              )}
            </Section>

            <Section title="Redirect URIs">
              {client.redirectURIs && client.redirectURIs.length > 0 ? (
                <ul className="space-y-1">
                  {client.redirectURIs.map((u) => (
                    <li key={u} className="font-mono text-xs break-all text-foreground">
                      {u}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">None registered.</p>
              )}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono text-foreground break-all" : "text-foreground capitalize"}>
        {value}
      </span>
    </div>
  );
}
